import * as THREE from 'three';

import { PAWN_HEIGHT, PlayerMarkers } from '../game/players/PlayerMarkers';
import { screenPlacer } from './ScreenPoint';

/**
 * Names above the pawns.
 *
 * HTML over the canvas, not sprites in the scene, and that is a decision rather
 * than a shortcut. The game renders into a small buffer scaled up by a whole
 * number, which is what makes the pixels look deliberate — and it is exactly
 * what would make text unreadable, since a name drawn into that buffer gets
 * blown up three times along with everything else. Names are interface, not
 * world: they live above the canvas at the screen's own resolution.
 *
 * The cost is that they do not sit behind hills the way a sprite would. For a
 * label whose whole job is to be readable, that is the right side of the trade.
 */

/**
 * Above the pawn's middle, in world units: half the pawn to its top and the
 * same gap above it that the label always had.
 */
const LIFT = PAWN_HEIGHT / 2 + 0.2;

export class NameTags {
  private readonly root = document.createElement('div');
  private readonly tags = new Map<string, HTMLElement>();
  private readonly projected = new THREE.Vector3();

  constructor(
    private readonly container: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
    private readonly markers: PlayerMarkers
  ) {
    this.root.className = 'name-tags';
    this.container.appendChild(this.root);
  }

  /**
   * `extra` to podpisy spoza rejestru pionków — dziś jeden: **własne imię nad
   * statkiem, którym się płynie**. Pionka wtedy nie ma na mapie (jest pod
   * pokładem), a podpis ma iść za tym, co widać. Cudzych załóg to nie dotyczy:
   * kto płynie, nie widać, i tak było ustalone.
   */
  update(
    camera: THREE.OrthographicCamera,
    extra: { playerId: string; username: string; position: THREE.Vector3 }[] = []
  ): void {
    // The canvas sits in the corner of the container and carries a border, so
    // positions are measured against the canvas rather than the window. That
    // arithmetic is shared with the progress bars over buildings — see
    // `ScreenPoint`.
    const place = screenPlacer(this.container, this.canvas, camera);
    const seen = new Set<string>();

    for (const { playerId, username, position } of [...this.markers.labels(), ...extra]) {
      seen.add(playerId);

      let tag = this.tags.get(playerId);
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'name-tag';
        tag.textContent = username;
        this.root.appendChild(tag);
        this.tags.set(playerId, tag);
      }

      this.projected.copy(position);
      this.projected.y += LIFT;

      // Outside the frame, or behind the camera: hidden rather than clamped to
      // an edge, where it would name somebody who is not there.
      const at = place(this.projected);
      tag.style.display = at ? 'block' : 'none';
      if (at) {
        tag.style.transform = `translate(${Math.round(at.x)}px, ${Math.round(at.y)}px) translate(-50%, -100%)`;
      }
    }

    for (const [playerId, tag] of this.tags) {
      if (seen.has(playerId)) continue;
      tag.remove();
      this.tags.delete(playerId);
    }
  }

  dispose(): void {
    this.root.remove();
    this.tags.clear();
  }
}
